import { useMemo, useState, useCallback, useEffect } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

/**
 * Shared sortable table primitives.
 *
 * Usage:
 *   const { sortKey, sortDir, onSort, sorted } = useSortableRows(rows, {
 *     name: (r) => (r.name || "").toLowerCase(),
 *     hours: (r) => Number(r.total_hours) || 0,
 *   }, { defaultKey: "name" });
 *
 *   <th><SortableHeader k="name" state={{ sortKey, sortDir, onSort }}>Name</SortableHeader></th>
 *
 * Accessors receive a row and return a comparable scalar (string / number / date).
 * Null/undefined values sort last regardless of direction (nulls-last).
 */
export function useSortableRows(rows, accessors, opts = {}) {
  const storageKey = opts.storageKey || null;

  const [sortKey, setSortKey] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(`sort_${storageKey}`);
        if (raw) return JSON.parse(raw).key || opts.defaultKey || null;
      } catch { /* ignore */ }
    }
    return opts.defaultKey || null;
  });
  const [sortDir, setSortDir] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(`sort_${storageKey}`);
        if (raw) return JSON.parse(raw).dir || opts.defaultDir || "asc";
      } catch { /* ignore */ }
    }
    return opts.defaultDir || "asc";
  });

  // Persist any change back to localStorage so the choice sticks per-user per-table.
  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(`sort_${storageKey}`, JSON.stringify({ key: sortKey, dir: sortDir }));
    } catch { /* quota */ }
  }, [storageKey, sortKey, sortDir]);

  const onSort = useCallback((k) => {
    setSortKey((prev) => {
      if (prev === k) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return k;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey || !accessors[sortKey]) return rows;
    const acc = accessors[sortKey];
    const dirMul = sortDir === "asc" ? 1 : -1;
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      const aNil = av === null || av === undefined || av === "";
      const bNil = bv === null || bv === undefined || bv === "";
      if (aNil && bNil) return 0;
      if (aNil) return 1;   // nulls last, regardless of dir
      if (bNil) return -1;
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
    return arr;
  }, [rows, sortKey, sortDir, accessors]);

  return { sortKey, sortDir, onSort, sorted };
}

/**
 * Clickable table header. Wraps the label in a button that toggles sort.
 * Renders inline testid `sort-{k}` so tests can target reliably.
 */
export function SortableHeader({ k, state, align = "left", children, className = "" }) {
  const { sortKey, sortDir, onSort } = state || {};
  const active = sortKey === k;
  const Icon = !active ? ArrowUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button
        type="button"
        data-testid={`sort-${k}`}
        onClick={() => onSort && onSort(k)}
        className={`inline-flex items-center gap-1 label-mute hover:text-white transition-colors ${active ? "text-[#FF3B30]" : ""}`}
      >
        {children}
        <Icon className={`size-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
}
